// @todo: Migrate to @wandb/components. For more details see:
// https://www.notion.so/wandbai/Migrating-components-207e2f5c7ef380129201f72a5770a8db
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button as WeaveButton} from '@wandb/weave/components';
import * as Dialog from '@wandb/weave/components/Dialog';
import {Icon as WeaveIcon} from '@wandb/weave/components/Icon';
import {clsx} from 'clsx';
import React, {ChangeEventHandler} from 'react';

import {Banner, BannerFlexWrapper, BannerTextWrapper} from './Banner';
import {SemanticLoader as Loader} from './utility/InstrumentedLoader';

type Error = {
  message: string;
};

export type DeleteProgress = {
  total: number;
  finished: number;
  failed: number;
  errors: Error[]; // TODO: types
  phase:
    | 'confirm'
    | 'complete'
    | 'failed'
    | 'files'
    | 'projects'
    | 'runs'
    | 'artifacts'
    | 'artifact_versions'
    | 'folders';
};

const DARK_BANNER_STYLE = 'dark:bg-red-700/[.24] dark:text-red-400';
const DARK_INPUT_STYLE =
  'dark:border-moon-750 dark:bg-moon-900 dark:placeholder:text-moon-500';
const LIGHT_INPUT_STYLE = 'border-moon-250 placeholder:text-moon-500';
const INPUT_STYLE = 'h-40 w-full rounded border px-12 py-8 outline-none';

type StorageConfirmDeleteModalProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  summary: React.ReactNode;
  onConfirmChange: ChangeEventHandler<HTMLInputElement>;
  isDeleteButtonEnabled: boolean;
  deleteProgress?: DeleteProgress;
  onConfirm: () => void;
  isMutationLoading: boolean;
  hasProtectedArtifact?: boolean;
  hasProtectedArtifactLoading?: boolean;
  deleteText?: string;
};

export const StorageConfirmDeleteModal = ({
  isOpen,
  onOpenChange,
  summary,
  onConfirmChange,
  isDeleteButtonEnabled,
  deleteProgress,
  onConfirm,
  isMutationLoading,
  hasProtectedArtifact,
  hasProtectedArtifactLoading,
  deleteText = 'all',
}: StorageConfirmDeleteModalProps) => {
  const DELETE_CONFIRM_TEXT = `DELETE ${deleteText.toUpperCase()}`;
  return (
    <Dialog.Root open={isOpen} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay />
        <Dialog.Content
          className="w-[600px] rounded-lg"
          onInteractOutside={e => {
            e.preventDefault();
          }}>
          <Dialog.Title>Are you absolutely sure?</Dialog.Title>
          {hasProtectedArtifact ? (
            <Banner variant="error" className="mt-16">
              <BannerFlexWrapper>
                <div>
                  <WeaveIcon name="warning" />
                </div>
                <BannerTextWrapper>
                  You are unable to delete this project until all its artifacts
                  have been unlinked from protected registry collections.
                </BannerTextWrapper>
              </BannerFlexWrapper>
            </Banner>
          ) : (
            <Banner
              variant="error"
              className={clsx('mt-16', DARK_BANNER_STYLE)}>
              <div className="flex items-center">
                <div className="h-18 w-18">
                  <WeaveIcon name="warning-alt" />
                </div>
                <div className="pl-16">
                  This cannot be undone. If you proceed, this{' '}
                  {deleteText !== 'all' ? deleteText.toLowerCase() : ''} will be
                  permanently deleted and you will not be able to get it back.
                </div>
              </div>
            </Banner>
          )}
          <Dialog.Description className="pt-16" data-test="delete-summary">
            This will permanently delete {summary}.
          </Dialog.Description>
          <Dialog.Description className="pt-16">
            Please type <strong>{DELETE_CONFIRM_TEXT}</strong> to confirm.
          </Dialog.Description>
          <input
            data-test="delete-all-input"
            className={clsx(INPUT_STYLE, LIGHT_INPUT_STYLE, DARK_INPUT_STYLE)}
            placeholder={DELETE_CONFIRM_TEXT}
            onChange={onConfirmChange}
            disabled={hasProtectedArtifactLoading || hasProtectedArtifact}
          />
          {deleteProgress != null && deleteProgress.errors.length > 0 && (
            <Banner
              variant="error"
              className={clsx(
                'mt-16 flex flex-col items-start leading-[140%]',
                DARK_BANNER_STYLE
              )}>
              <strong>Errors were encountered while deleting objects:</strong>
              <ul className="list-disc pl-16">
                {deleteProgress.errors.map((e, i) => (
                  <li key={`error_${i}`}>{e.message}</li>
                ))}
              </ul>
            </Banner>
          )}
          <div className="mb-8 mt-24 flex items-center">
            <WeaveButton
              data-test="delete-all-button"
              variant={isDeleteButtonEnabled ? 'destructive' : 'secondary'}
              disabled={
                !isDeleteButtonEnabled ||
                (deleteProgress != null && deleteProgress.errors.length > 0) ||
                hasProtectedArtifactLoading ||
                hasProtectedArtifact
              }
              size="large"
              onClick={onConfirm}
              className={
                isDeleteButtonEnabled
                  ? ''
                  : 'dark:bg-moonbeam/[.03] dark:text-moon-650 dark:opacity-100 dark:filter-none'
              }>
              <div className="flex">
                I understand the consequences, delete {deleteText}
                {deleteProgress != null && deleteProgress.total > 0 && (
                  <div className="ml-8 flex items-center">
                    <Loader
                      active
                      size="tiny"
                      inline
                      name="delete-all-loader"
                    />
                  </div>
                )}
              </div>
            </WeaveButton>
            <Dialog.Close className="ml-8">
              <WeaveButton
                className="dark:text-moon-200/100 dark:filter-none hover:dark:text-moon-200"
                variant="ghost"
                size="large"
                disabled={isMutationLoading}>
                Cancel
              </WeaveButton>
            </Dialog.Close>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
